/**
 * Generic Drill-Down Hooks
 *
 * Provides dynamic dimension detection, filtering, grouping, and comparison
 * for any drill-down data set. Every column can be a filter or group-by dimension.
 */

import { useMemo, useState, useCallback } from 'react';

// Types
export interface DrillDownRow {
  [key: string]: string | number | null | undefined;
  Valor?: number | null;
  Data?: string | null;
}

export interface Dimension {
  key: string;
  label: string;
  type: 'string' | 'number' | 'date' | 'date-month';
  uniqueValues: string[];
  valueCount: number;
}

export interface GroupedRow {
  dimensionValue: string;
  count: number;
  total: number;
  percentage: number;
  rows: DrillDownRow[];
}

export interface FilterState {
  [dimensionKey: string]: string[];
}

export interface ComparisonPeriod {
  startDate: string;
  endDate: string;
  label: string;
}

// Dimension configuration - maps field names to display labels
const DIMENSION_CONFIG: Record<string, { label: string; type: 'string' | 'number' | 'date' | 'date-month' }> = {
  // Entity dimensions
  Fornecedor: { label: 'Fornecedor', type: 'string' },
  Cliente: { label: 'Cliente', type: 'string' },
  Colaborador: { label: 'Colaborador', type: 'string' },

  // Organizational dimensions
  CentroResultado: { label: 'Centro de Resultado', type: 'string' },
  Job: { label: 'Job', type: 'string' },
  PIT: { label: 'PIT', type: 'string' },

  // Classification dimensions
  Natureza: { label: 'Natureza', type: 'string' },
  CategoriaGerencial: { label: 'Categoria Gerencial', type: 'string' },
  Tipo: { label: 'Tipo', type: 'string' },

  // Financial dimensions
  Banco: { label: 'Banco', type: 'string' },

  // Document dimensions
  NotaFiscal: { label: 'Nota Fiscal', type: 'string' },
  Documento: { label: 'Documento', type: 'string' },
  NumeroTitulo: { label: 'No Titulo', type: 'string' },

  // ID dimensions (usually for filtering, not grouping)
  CodigoCliente: { label: 'Cod. Cliente', type: 'string' },
  CodigoFornecedor: { label: 'Cod. Fornecedor', type: 'string' },
  CNPJ: { label: 'CNPJ', type: 'string' },
  CNPJFornecedor: { label: 'CNPJ Fornecedor', type: 'string' },
  CPF_CNPJ: { label: 'CPF/CNPJ', type: 'string' },
};

// Fields to exclude from dimension detection (always present but not useful for grouping)
const EXCLUDED_FIELDS = ['Valor', 'Data', 'Horas', 'Competencia', 'Prefixo', 'Serie', 'Parcela', 'Historico', 'Descricao', 'Produto'];

/**
 * Detect available dimensions from the data
 * Only includes dimensions that have actual non-null values
 */
export function useDrillDownDimensions(data: DrillDownRow[]): {
  dimensions: Dimension[];
  monthDimension: Dimension | null;
  dayDimension: Dimension | null;
  allDimensions: Dimension[];
} {
  const dimensions = useMemo<Dimension[]>(() => {
    if (!data || data.length === 0) return [];

    const dims: Dimension[] = [];
    const checkedKeys = new Set<string>();

    // Check each row for potential dimension fields
    for (const row of data) {
      for (const key of Object.keys(row)) {
        if (checkedKeys.has(key)) continue;
        if (EXCLUDED_FIELDS.includes(key)) continue;

        checkedKeys.add(key);

        // Get config or create default
        const config = DIMENSION_CONFIG[key] || { label: key, type: 'string' as const };

        // Collect unique values
        const uniqueValues = new Set<string>();
        for (const r of data) {
          const val = r[key];
          if (val != null && val !== '' && String(val).trim() !== '') {
            uniqueValues.add(String(val).trim());
          }
        }

        // Only include if there are actual values and more than 1 unique value
        // (single value dimensions are not useful for filtering/grouping)
        if (uniqueValues.size > 0) {
          dims.push({
            key,
            label: config.label,
            type: config.type,
            uniqueValues: Array.from(uniqueValues).sort(),
            valueCount: uniqueValues.size,
          });
        }
      }
    }

    // Sort by value count descending (most useful dimensions first)
    return dims.sort((a, b) => {
      // Prioritize known dimensions
      const aKnown = DIMENSION_CONFIG[a.key] ? 1 : 0;
      const bKnown = DIMENSION_CONFIG[b.key] ? 1 : 0;
      if (aKnown !== bKnown) return bKnown - aKnown;

      // Then by value count (prefer dimensions with 2-50 unique values)
      const aScore = a.valueCount >= 2 && a.valueCount <= 50 ? 100 - a.valueCount : a.valueCount;
      const bScore = b.valueCount >= 2 && b.valueCount <= 50 ? 100 - b.valueCount : b.valueCount;
      return bScore - aScore;
    });
  }, [data]);

  // Derive month dimension from Data field if present
  const monthDimension = useMemo<Dimension | null>(() => {
    if (!data || data.length === 0) return null;

    const months = new Set<string>();
    for (const row of data) {
      if (row.Data) {
        const date = new Date(row.Data);
        if (!isNaN(date.getTime())) {
          const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          months.add(monthKey);
        }
      }
    }

    if (months.size > 1) {
      return {
        key: '_month',
        label: 'Mes',
        type: 'date-month',
        uniqueValues: Array.from(months).sort(),
        valueCount: months.size,
      };
    }
    return null;
  }, [data]);

  // Derive day dimension from Data field if present
  const dayDimension = useMemo<Dimension | null>(() => {
    if (!data || data.length === 0) return null;

    const days = new Set<string>();
    for (const row of data) {
      if (row.Data) {
        const date = new Date(row.Data);
        if (!isNaN(date.getTime())) {
          const dayKey = date.toISOString().split('T')[0]; // YYYY-MM-DD
          days.add(dayKey);
        }
      }
    }

    if (days.size > 1) {
      return {
        key: '_day',
        label: 'Dia',
        type: 'date',
        uniqueValues: Array.from(days).sort(),
        valueCount: days.size,
      };
    }
    return null;
  }, [data]);

  // Build allDimensions with date-based dimensions
  const allDimensions = useMemo(() => {
    const result = [...dimensions];
    if (monthDimension) result.push(monthDimension);
    if (dayDimension) result.push(dayDimension);
    return result;
  }, [dimensions, monthDimension, dayDimension]);

  return {
    dimensions,
    monthDimension,
    dayDimension,
    allDimensions,
  };
}

/**
 * Filter data based on active filters and text search
 */
export function useDrillDownFiltering(
  data: DrillDownRow[],
  filters: FilterState,
  textSearch?: string
): { filteredData: DrillDownRow[]; filterSummary: { total: number; filtered: number; activeFilterCount: number; isFiltered: boolean; percentage: number } } {
  const filteredData = useMemo(() => {
    if (!data || data.length === 0) return [];

    let result = data;

    // Apply text search filter first
    if (textSearch && textSearch.trim()) {
      const searchLower = textSearch.toLowerCase().trim();
      result = result.filter(row => {
        // Search across all string fields
        for (const value of Object.values(row)) {
          if (value != null && String(value).toLowerCase().includes(searchLower)) {
            return true;
          }
        }
        return false;
      });
    }

    // Then apply dimension filters
    if (Object.keys(filters).length === 0) return result;

    return result.filter(row => {
      for (const [key, values] of Object.entries(filters)) {
        if (!values || values.length === 0) continue;

        // Special handling for month dimension
        if (key === '_month') {
          if (row.Data) {
            const date = new Date(row.Data);
            const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
            if (!values.includes(monthKey)) return false;
          } else {
            return false;
          }
          continue;
        }

        // Special handling for day dimension
        if (key === '_day') {
          if (row.Data) {
            const date = new Date(row.Data);
            const dayKey = date.toISOString().split('T')[0];
            if (!values.includes(dayKey)) return false;
          } else {
            return false;
          }
          continue;
        }

        const rowValue = row[key];
        if (rowValue == null || !values.includes(String(rowValue).trim())) {
          return false;
        }
      }
      return true;
    });
  }, [data, filters, textSearch]);

  const filterSummary = useMemo(() => {
    const total = data?.length || 0;
    const filtered = filteredData.length;
    const activeFilterCount = Object.values(filters).filter(v => v && v.length > 0).length;
    const hasTextSearch = !!(textSearch && textSearch.trim());
    return {
      total,
      filtered,
      activeFilterCount,
      isFiltered: activeFilterCount > 0 || hasTextSearch,
      percentage: total > 0 ? (filtered / total) * 100 : 0,
    };
  }, [data, filteredData, filters, textSearch]);

  return { filteredData, filterSummary };
}

/**
 * Group data by a dimension
 */
export function useDrillDownGrouping(
  data: DrillDownRow[],
  groupByKey: string | null,
  sortBy: 'total' | 'count' | 'name' = 'total',
  sortOrder: 'asc' | 'desc' = 'desc'
): { groupedData: GroupedRow[]; groupSummary: { groupCount: number; totalValue: number; totalCount: number } } {
  const groupedData = useMemo<GroupedRow[]>(() => {
    if (!data || data.length === 0 || !groupByKey) return [];

    const groups = new Map<string, { count: number; total: number; rows: DrillDownRow[] }>();
    let grandTotal = 0;

    for (const row of data) {
      let groupValue: string;

      // Special handling for month dimension
      if (groupByKey === '_month') {
        if (row.Data) {
          const date = new Date(row.Data);
          groupValue = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
        } else {
          groupValue = '(sem data)';
        }
      // Special handling for day dimension
      } else if (groupByKey === '_day') {
        if (row.Data) {
          const date = new Date(row.Data);
          groupValue = date.toISOString().split('T')[0]; // YYYY-MM-DD
        } else {
          groupValue = '(sem data)';
        }
      } else {
        const val = row[groupByKey];
        groupValue = val != null && String(val).trim() !== '' ? String(val).trim() : '(vazio)';
      }

      const valor = Number(row.Valor) || 0;
      grandTotal += valor;

      const existing = groups.get(groupValue) || { count: 0, total: 0, rows: [] };
      existing.count++;
      existing.total += valor;
      existing.rows.push(row);
      groups.set(groupValue, existing);
    }

    const result: GroupedRow[] = Array.from(groups.entries()).map(([dimensionValue, groupData]) => ({
      dimensionValue,
      count: groupData.count,
      total: groupData.total,
      percentage: grandTotal > 0 ? (groupData.total / grandTotal) * 100 : 0,
      rows: groupData.rows,
    }));

    // Sort
    result.sort((a, b) => {
      let comparison: number;
      switch (sortBy) {
        case 'total':
          comparison = a.total - b.total;
          break;
        case 'count':
          comparison = a.count - b.count;
          break;
        case 'name':
          comparison = a.dimensionValue.localeCompare(b.dimensionValue, 'pt-BR');
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'desc' ? -comparison : comparison;
    });

    return result;
  }, [data, groupByKey, sortBy, sortOrder]);

  const groupSummary = useMemo(() => {
    const total = groupedData.reduce((sum, g) => sum + g.total, 0);
    const count = groupedData.reduce((sum, g) => sum + g.count, 0);
    return {
      groupCount: groupedData.length,
      totalValue: total,
      totalCount: count,
    };
  }, [groupedData]);

  return { groupedData, groupSummary };
}

/**
 * Main drill-down state hook - combines all functionality
 */
export function useDrillDown(initialData: DrillDownRow[]) {
  // State
  const [filters, setFilters] = useState<FilterState>({});
  const [textSearch, setTextSearch] = useState<string>('');
  const [groupByKey, setGroupByKey] = useState<string | null>(null);
  const [sortBy, setSortBy] = useState<'total' | 'count' | 'name'>('total');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showTransactions, setShowTransactions] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

  // Derived state
  const { dimensions, monthDimension, allDimensions } = useDrillDownDimensions(initialData);
  const { filteredData, filterSummary } = useDrillDownFiltering(initialData, filters, textSearch);
  const { groupedData, groupSummary } = useDrillDownGrouping(filteredData, groupByKey, sortBy, sortOrder);

  // Actions
  const setFilter = useCallback((dimensionKey: string, values: string[]) => {
    setFilters(prev => ({
      ...prev,
      [dimensionKey]: values,
    }));
  }, []);

  const clearFilter = useCallback((dimensionKey: string) => {
    setFilters(prev => {
      const next = { ...prev };
      delete next[dimensionKey];
      return next;
    });
  }, []);

  const clearAllFilters = useCallback(() => {
    setFilters({});
    setTextSearch('');
  }, []);

  const toggleSort = useCallback((newSortBy: 'total' | 'count' | 'name') => {
    if (sortBy === newSortBy) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(newSortBy);
      setSortOrder('desc');
    }
  }, [sortBy]);

  const drillIntoGroup = useCallback((groupValue: string) => {
    if (!groupByKey) return;

    // Add current groupBy as filter
    setFilters(prev => ({
      ...prev,
      [groupByKey]: [groupValue],
    }));

    // Clear groupBy (show transactions)
    setGroupByKey(null);
    setSelectedGroup(groupValue);
  }, [groupByKey]);

  const drillOut = useCallback(() => {
    // Remove the last added filter
    const filterKeys = Object.keys(filters);
    if (filterKeys.length > 0) {
      const lastKey = filterKeys[filterKeys.length - 1];
      clearFilter(lastKey);
      setGroupByKey(lastKey);
    }
    setSelectedGroup(null);
  }, [filters, clearFilter]);

  // Calculate totals
  const totals = useMemo(() => {
    const total = filteredData.reduce((sum, row) => sum + (Number(row.Valor) || 0), 0);
    return {
      totalValue: total,
      totalCount: filteredData.length,
    };
  }, [filteredData]);

  return {
    // Data
    rawData: initialData,
    filteredData,
    groupedData,

    // Dimensions
    dimensions,
    monthDimension,
    allDimensions,

    // State
    filters,
    textSearch,
    groupByKey,
    sortBy,
    sortOrder,
    showTransactions,
    selectedGroup,

    // Summaries
    filterSummary,
    groupSummary,
    totals,

    // Actions
    setFilter,
    setTextSearch,
    clearFilter,
    clearAllFilters,
    setGroupByKey,
    toggleSort,
    setSortOrder,
    setShowTransactions,
    drillIntoGroup,
    drillOut,
  };
}

/**
 * Comparison hook - compare two periods
 */
export function useDrillDownComparison(
  currentData: DrillDownRow[],
  comparisonData: DrillDownRow[] | null,
  groupByKey: string | null
): {
  comparison: {
    deltas: Array<{
      dimensionValue: string;
      currentValue: number;
      comparisonValue: number;
      delta: number;
      deltaPercentage: number;
    }>;
    summary: {
      currentTotal: number;
      comparisonTotal: number;
      totalDelta: number;
      totalDeltaPercentage: number;
    };
  } | null;
} {
  const comparison = useMemo(() => {
    if (!comparisonData || !groupByKey) return null;

    // Group both datasets
    const groupData = (data: DrillDownRow[]) => {
      const groups = new Map<string, number>();
      for (const row of data) {
        let groupValue: string;
        if (groupByKey === '_month') {
          if (row.Data) {
            const date = new Date(row.Data);
            groupValue = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
          } else {
            continue;
          }
        } else if (groupByKey === '_day') {
          if (row.Data) {
            const date = new Date(row.Data);
            groupValue = date.toISOString().split('T')[0];
          } else {
            continue;
          }
        } else {
          const val = row[groupByKey];
          groupValue = val != null ? String(val).trim() : '(vazio)';
        }
        const valor = Number(row.Valor) || 0;
        groups.set(groupValue, (groups.get(groupValue) || 0) + valor);
      }
      return groups;
    };

    const currentGroups = groupData(currentData);
    const comparisonGroups = groupData(comparisonData);

    // Calculate deltas
    const allKeys = new Set([...currentGroups.keys(), ...comparisonGroups.keys()]);
    const deltas: Array<{
      dimensionValue: string;
      currentValue: number;
      comparisonValue: number;
      delta: number;
      deltaPercentage: number;
    }> = [];

    for (const key of allKeys) {
      const current = currentGroups.get(key) || 0;
      const comp = comparisonGroups.get(key) || 0;
      const delta = current - comp;
      const deltaPercentage = comp > 0 ? (delta / comp) * 100 : current > 0 ? 100 : 0;

      deltas.push({
        dimensionValue: key,
        currentValue: current,
        comparisonValue: comp,
        delta,
        deltaPercentage,
      });
    }

    // Sort by absolute delta
    deltas.sort((a, b) => Math.abs(b.delta) - Math.abs(a.delta));

    // Summary
    const currentTotal = Array.from(currentGroups.values()).reduce((a, b) => a + b, 0);
    const comparisonTotal = Array.from(comparisonGroups.values()).reduce((a, b) => a + b, 0);
    const totalDelta = currentTotal - comparisonTotal;
    const totalDeltaPercentage = comparisonTotal > 0 ? (totalDelta / comparisonTotal) * 100 : 0;

    return {
      deltas,
      summary: {
        currentTotal,
        comparisonTotal,
        totalDelta,
        totalDeltaPercentage,
      },
    };
  }, [currentData, comparisonData, groupByKey]);

  return { comparison };
}
